// Payments-tab advanced table — replaces the old card-list UI with the same
// Reporting-style DataGridPro (ReportDataGrid) used across the app, so every
// table in the CRM looks/behaves the same (column reorder/width/visibility
// persistence, toolbar, CSV export). Client mode: a lead has few payments.
//
// Unlike every other ReportDataGrid table (which live on their own full-width
// report page), this one lives inside a narrow lead-detail tab — so columns
// use `flex` + a sensible `minWidth` (see the ReportDataGrid.jsx comment on
// `orderedColumns`: a column declaring `flex` opts out of the persisted
// fixed-pixel-width override) to fit the tab instead of a report page's
// width, with horizontal scroll only kicking in below that.
import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import Label from 'src/components/Label';
import ReportDataGrid from 'src/components/table/ReportDataGrid';
import { derivePaymentCardStatus } from './paymentCardStatus';
import {
    formatAmount,
    formatDate,
    formatRelativeToNow,
    formatLastSent,
    requestedBy,
    titleCase,
    paymentMethodLabel,
    STATUS_PILL,
    statusPillText,
    APPOINTMENT_STATUS_CHIP,
    formatAppointmentSummaryLine,
} from './paymentPresentation';
import { DeliveryChipRow, newestDeliveryOverall } from './deliveryChips';
import usePaymentSessions from './usePaymentSessions';
import PaymentActionsCell from './PaymentActionsCell';
import PaymentTimelinePanel from './PaymentTimelinePanel';

// v3: at a 1454px lead-detail-tab width there simply isn't room for every
// column at once — Method and Link expires are now folded into muted
// captions under the Status pill instead (see StatusCell) and hidden from
// the default column set, though both stay available/re-addable via the
// Columns menu for anyone who wants them as real sortable/exportable
// columns. Fresh key again (not a migration — see the v2 comment history in
// git blame) so nobody's already-saved v2 visibility model masks the new
// default-hidden columns.
// v4: adds the Appointment column (contract: Payment gains `appointment` /
// `appointment_summary` — see AppointmentCell below) between Treatment and
// Status. Fresh key (not a migration) so nobody's already-saved v3
// visibility model masks the new default-visible column — same rationale as
// the v2→v3 bump above.
const TABLE_NAME = 'lead-payments-v4';
const FIELDS = ['amountType', 'treatment', 'appointment', 'status', 'method', 'requested', 'paid', 'delivery', 'expires', 'actions'];
const ACTIONS_WIDTH = 340;

function AppointmentCell({ payment }) {
    const summary = payment?.appointment_summary;
    if (!summary) {
        return <Typography variant="body2" color="text.secondary" data-testid={`appointment-cell-${payment?.id}`}>—</Typography>;
    }
    const chip = APPOINTMENT_STATUS_CHIP[summary.status] || null;
    return (
        <Stack spacing={0.25} sx={{ py: 0.5 }} data-testid={`appointment-cell-${payment?.id}`}>
            <Typography variant="body2">{formatAppointmentSummaryLine(summary)}</Typography>
            {chip && (
                <Label color={chip.color} variant="ghost" data-testid={`appointment-status-chip-${payment?.id}`}>
                    {chip.label}
                </Label>
            )}
        </Stack>
    );
}
AppointmentCell.propTypes = { payment: PropTypes.object.isRequired };

function buildDefaults(showTreatment) {
    return {
        columns: FIELDS,
        columnOrder: FIELDS,
        // Method and Link expires are surfaced as captions under the Status
        // pill instead (see StatusCell) — hidden by default so the default
        // column set actually fits a lead-detail tab, but still listed in
        // FIELDS/columns so they remain toggleable in the Columns menu.
        columnVisibilityModel: { treatment: showTreatment, method: false, expires: false },
        // Only 'actions' gets a persisted-width entry — every other column
        // below declares its own `flex`, which (see ReportDataGrid.jsx)
        // opts it out of the persisted fixed-pixel-width path entirely.
        // 'actions' is pinned right and must stay a real fixed width: a
        // flex column inside a pinned section doesn't reliably reserve its
        // own space against the scrollable middle section, which is what
        // was making the Delivery column unreachable.
        columnWidths: { actions: ACTIONS_WIDTH },
        pinnedColumns: { left: [], right: ['actions'] },
        sortModel: [],
        filterModel: { items: [] },
        paginationModel: { page: 0, pageSize: 25 },
        // Deliberately NOT `requiredFields: ['actions']` — that mechanism
        // (see sanitizePins in useReportTablePreferences.js) forces every
        // required field into the LEFT pin unconditionally, which is what
        // pinned Actions first/left in the initial version of this table
        // despite `pinnedColumns.right` naming it. Actions stays hideable
        // (nobody needs to hide it in practice) in exchange for actually
        // landing on the right, which is the only place it makes sense.
    };
}

// Status pill + (when the columns they'd otherwise live in are hidden by
// default, see buildDefaults) a muted caption line per extra fact: the
// recorded method for a non-Stripe payment ("Phone"), and the link's
// relative expiry for an awaiting/expired link payment ("expires in 2
// hours" / "expired 2 hours ago") — both mirror what the old card's line 2
// used to show inline, just condensed for the table's Status column.
function StatusCell({ payment, ctx }) {
    const status = derivePaymentCardStatus(payment);
    const pill = STATUS_PILL[status] || STATUS_PILL.noLink;
    const isLinkPayment = ['awaiting', 'expired', 'failed'].includes(status);
    const { latestSession } = usePaymentSessions(payment, { ...ctx, eager: isLinkPayment });

    const methodLabel = paymentMethodLabel(payment);
    const showMethodCaption = methodLabel && methodLabel !== 'Stripe link' && methodLabel !== '—';

    let expiryCaption = null;
    if (isLinkPayment && !payment?.paid_at && latestSession?.expires_at) {
        expiryCaption = status === 'expired'
            ? formatRelativeToNow(latestSession.expires_at)
            : `expires ${formatRelativeToNow(latestSession.expires_at)}`;
    }

    return (
        <Stack spacing={0.25} sx={{ py: 0.5 }}>
            <Label color={pill.color} variant="ghost" data-testid={`payment-status-pill-${payment?.id}`}>
                {pill.icon} {statusPillText(status, payment)}
            </Label>
            {showMethodCaption && (
                <Typography variant="caption" color="text.secondary" data-testid={`status-method-caption-${payment?.id}`}>
                    {methodLabel}
                </Typography>
            )}
            {expiryCaption && (
                <Typography variant="caption" color="text.secondary" data-testid={`status-expiry-caption-${payment?.id}`}>
                    {expiryCaption}
                </Typography>
            )}
        </Stack>
    );
}
StatusCell.propTypes = { payment: PropTypes.object.isRequired, ctx: PropTypes.object.isRequired };

// Compact two-line date cells: the date on line 1, a muted "by X" (or, for
// a Stripe-paid payment, "· Stripe") on line 2 — narrower than one long
// line, which is what this table needs at a lead-detail-tab width.
function RequestedCell({ payment }) {
    return (
        <Stack spacing={0} sx={{ py: 0.5 }} data-testid={`requested-cell-${payment?.id}`}>
            <Typography variant="body2">{formatDate(payment?.created_at, 'DD MMM, HH:mm')}</Typography>
            <Typography variant="caption" color="text.secondary">by {requestedBy(payment)}</Typography>
        </Stack>
    );
}
RequestedCell.propTypes = { payment: PropTypes.object.isRequired };

function PaidCell({ payment }) {
    const status = derivePaymentCardStatus(payment);
    // A 'received' legacy row (Take Payment "£X over the phone/in practice")
    // has no paid_at yet — use created_at as the received date instead of
    // hiding the Paid column entirely (this is already-received money).
    const showsPaidInfo = Boolean(payment?.paid_at) || status === 'received';
    if (!showsPaidInfo) return null;
    const displayDate = payment?.paid_at || payment?.created_at;
    let suffix = null;
    if (status === 'received') suffix = `by ${payment?.paid_by_display || payment?.created_by_display || 'someone'}`;
    else if (payment?.paid_manually) suffix = `by ${payment?.paid_by_display || 'someone'}`;
    else if (paymentMethodLabel(payment) === 'Stripe link') suffix = '· Stripe';
    return (
        <Stack spacing={0} sx={{ py: 0.5 }} data-testid={`paid-cell-${payment?.id}`}>
            <Typography variant="body2">{formatDate(displayDate, 'DD MMM, HH:mm')}</Typography>
            {suffix && <Typography variant="caption" color="text.secondary">{suffix}</Typography>}
        </Stack>
    );
}
PaidCell.propTypes = { payment: PropTypes.object.isRequired };

function LinkExpiresCell({ payment, ctx }) {
    const status = derivePaymentCardStatus(payment);
    const isLinkPayment = ['awaiting', 'expired', 'failed'].includes(status);
    const { latestSession } = usePaymentSessions(payment, { ...ctx, eager: isLinkPayment });
    if (!isLinkPayment || !latestSession?.expires_at || payment?.paid_at) return null;
    return (
        <Typography variant="body2" color="text.secondary" data-testid={`expires-cell-${payment?.id}`}>
            {formatRelativeToNow(latestSession.expires_at)}
        </Typography>
    );
}
LinkExpiresCell.propTypes = { payment: PropTypes.object.isRequired, ctx: PropTypes.object.isRequired };

// Chips stacked vertically (one per channel) rather than in a wrapping row —
// a wrapping row was what truncated "WhatsApp"/"Email" mid-word once the
// column narrowed below both chips' combined width.
function DeliveryCell({ payment, ctx }) {
    const status = derivePaymentCardStatus(payment);
    const isLinkPayment = ['awaiting', 'expired', 'failed'].includes(status);
    const { sessions } = usePaymentSessions(payment, { ...ctx, eager: isLinkPayment });
    if (payment?.paid_at || status === 'received') return null;
    const latestSession = Array.isArray(sessions) && sessions.length > 0
        ? sessions.find((s) => String(s.id) === String(payment?.checkout_summary?.latest_checkout_session_id)) || sessions[0]
        : null;
    const deliveries = latestSession?.deliveries;
    const allDeliveries = Array.isArray(sessions) ? sessions.flatMap((s) => (Array.isArray(s?.deliveries) ? s.deliveries : [])) : [];
    const lastDelivery = newestDeliveryOverall(allDeliveries);
    const lastSentLabel = formatLastSent(lastDelivery?.created_at, payment?.created_at);
    const hasDeliveries = Array.isArray(deliveries) && deliveries.length > 0;
    if (!hasDeliveries && !lastSentLabel) return null;
    return (
        <Stack spacing={0.25} alignItems="flex-start" sx={{ py: 0.5 }} data-testid={`delivery-cell-${payment?.id}`}>
            <DeliveryChipRow deliveries={deliveries} />
            {lastSentLabel && (
                <Typography variant="caption" color="text.disabled" data-testid={`last-sent-${payment?.id}`}>
                    Last sent {lastSentLabel}
                </Typography>
            )}
        </Stack>
    );
}
DeliveryCell.propTypes = { payment: PropTypes.object.isRequired, ctx: PropTypes.object.isRequired };

export default function PaymentsTable({ payments, leadDetail, practiceId, leadId, treatmentIdFor, treatmentNameFor, showTreatment, onChanged }) {
    const columns = useMemo(() => {
        const ctxFor = (payment) => ({ practiceId, leadId, treatmentId: treatmentIdFor(payment) });

        return [
            {
                field: 'amountType',
                headerName: 'Amount · Type',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) => Number(params.row?.amount) || 0,
                valueFormatter: (params) => `£${formatAmount(params.value)} · ${titleCase(params.api.getRow(params.id)?.payment_type)}`,
                renderCell: (params) => (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        £{formatAmount(params.row.amount)} · {titleCase(params.row.payment_type)}
                    </Typography>
                ),
            },
            {
                field: 'treatment',
                headerName: 'Treatment',
                flex: 1,
                minWidth: 120,
                valueGetter: (params) => treatmentNameFor(params.row) || '',
            },
            {
                field: 'appointment',
                headerName: 'Appointment',
                flex: 1,
                minWidth: 150,
                valueGetter: (params) => formatAppointmentSummaryLine(params.row?.appointment_summary),
                valueFormatter: (params) => {
                    const summary = params.api.getRow(params.id)?.appointment_summary;
                    if (!summary) return '';
                    const chip = APPOINTMENT_STATUS_CHIP[summary.status];
                    return chip ? `${params.value} (${chip.label})` : params.value;
                },
                renderCell: (params) => <AppointmentCell payment={params.row} />,
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                minWidth: 140,
                valueGetter: (params) => derivePaymentCardStatus(params.row),
                valueFormatter: (params) => statusPillText(params.value, params.api.getRow(params.id)),
                renderCell: (params) => <StatusCell payment={params.row} ctx={ctxFor(params.row)} />,
            },
            {
                // Hidden by default (see buildDefaults) — its info is shown
                // as a caption under the Status pill instead — but stays a
                // real column so it's sortable/exportable via the Columns
                // menu for anyone who wants it back.
                field: 'method',
                headerName: 'Method',
                flex: 0.7,
                minWidth: 110,
                valueGetter: (params) => paymentMethodLabel(params.row),
            },
            {
                field: 'requested',
                headerName: 'Requested',
                flex: 1.1,
                minWidth: 140,
                valueGetter: (params) => {
                    const d = new Date(params.row?.created_at);
                    return Number.isNaN(d.valueOf()) ? 0 : d.valueOf();
                },
                valueFormatter: (params) => {
                    const row = params.api.getRow(params.id);
                    return `${formatDate(row?.created_at, 'DD MMM, HH:mm')} by ${requestedBy(row)}`;
                },
                renderCell: (params) => <RequestedCell payment={params.row} />,
            },
            {
                field: 'paid',
                headerName: 'Paid',
                flex: 1,
                minWidth: 130,
                valueGetter: (params) => {
                    const { row } = params;
                    const status = derivePaymentCardStatus(row);
                    const showsPaidInfo = Boolean(row?.paid_at) || status === 'received';
                    if (!showsPaidInfo) return null;
                    const d = new Date(row.paid_at || row.created_at);
                    return Number.isNaN(d.valueOf()) ? null : d.valueOf();
                },
                valueFormatter: (params) => {
                    if (!params.value) return '';
                    const row = params.api.getRow(params.id);
                    const status = derivePaymentCardStatus(row);
                    let suffix = '';
                    if (status === 'received') suffix = ` by ${row?.paid_by_display || row?.created_by_display || 'someone'}`;
                    else if (row?.paid_manually) suffix = ` by ${row?.paid_by_display || 'someone'}`;
                    else if (paymentMethodLabel(row) === 'Stripe link') suffix = ' · Stripe';
                    return `${formatDate(row?.paid_at || row?.created_at, 'DD MMM, HH:mm')}${suffix}`;
                },
                renderCell: (params) => <PaidCell payment={params.row} />,
            },
            {
                field: 'delivery',
                headerName: 'Delivery',
                flex: 1.2,
                minWidth: 170,
                sortable: false,
                filterable: false,
                valueGetter: () => '',
                renderCell: (params) => <DeliveryCell payment={params.row} ctx={ctxFor(params.row)} />,
            },
            {
                // Hidden by default (see buildDefaults) — shown as a caption
                // under the Status pill instead (awaiting/expired only) —
                // but stays a real column, toggleable via the Columns menu.
                field: 'expires',
                headerName: 'Link expires',
                flex: 0.8,
                minWidth: 120,
                sortable: false,
                filterable: false,
                valueGetter: () => '',
                renderCell: (params) => <LinkExpiresCell payment={params.row} ctx={ctxFor(params.row)} />,
            },
            {
                field: 'actions',
                headerName: 'Actions',
                // Pinned-right column — kept a real fixed width (not flex).
                // A flex column inside DataGridPro's pinned section doesn't
                // reliably reserve its own space against the scrollable
                // middle section, which was making the last scrollable
                // column (Delivery) unreachable.
                width: ACTIONS_WIDTH,
                sortable: false,
                filterable: false,
                disableExport: true,
                valueGetter: () => '',
                renderCell: (params) => (
                    <PaymentActionsCell
                        payment={params.row}
                        leadDetail={leadDetail}
                        practiceId={practiceId}
                        leadId={leadId}
                        treatmentId={treatmentIdFor(params.row)}
                        onChanged={onChanged}
                    />
                ),
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadDetail, practiceId, leadId, treatmentIdFor, treatmentNameFor, onChanged]);

    const defaults = useMemo(() => buildDefaults(showTreatment), [showTreatment]);

    // Measures the VISIBLE width of the grid's own container (not the
    // grid's internal scroll-content width, which can be wider) so the
    // detail-panel timeline can clamp itself to it — see the sticky-width
    // comment in PaymentTimelinePanel.jsx. Falls back to no clamp (null) if
    // ResizeObserver isn't available (older browsers, some test/JSDOM
    // environments).
    const containerRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(null);
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width;
            if (width) setGridWidth(width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'auto' }}>
            <ReportDataGrid
                tableName={TABLE_NAME}
                columns={columns}
                defaults={defaults}
                rows={payments}
                getRowId={(row) => row.id}
                getRowHeight={() => 'auto'}
                getDetailPanelContent={({ row }) => (
                    <PaymentTimelinePanel
                        payment={row}
                        practiceId={practiceId}
                        leadId={leadId}
                        treatmentId={treatmentIdFor(row)}
                        width={gridWidth}
                    />
                )}
                getDetailPanelHeight={() => 'auto'}
                toolbarVariant="campaign-manager"
                toolbarStart={<Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>Payments</Typography>}
                autoHeight
            />
        </Box>
    );
}

PaymentsTable.propTypes = {
    payments: PropTypes.array,
    leadDetail: PropTypes.object,
    practiceId: PropTypes.any,
    leadId: PropTypes.any,
    treatmentIdFor: PropTypes.func.isRequired,
    treatmentNameFor: PropTypes.func.isRequired,
    showTreatment: PropTypes.bool,
    onChanged: PropTypes.func,
};

PaymentsTable.defaultProps = {
    payments: [],
    leadDetail: null,
    practiceId: null,
    leadId: null,
    showTreatment: false,
    onChanged: null,
};
